import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type TextareaHTMLAttributes,
  type ReactNode,
} from 'react'
import { cx } from '../cx'
import { SectionLabel } from './SectionLabel'
import styles from './Textarea.module.css'

export interface TextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className'
> {
  /** Micro all-caps label above the field (spec §4 "Inputs & forms"). */
  label: string
  /** Validation or explanatory text below the field. */
  hint?: ReactNode
  /** Renders the field in an error state and announces `hint` as the message. */
  invalid?: boolean
  className?: string | undefined
}

/**
 * The multiline sibling of {@link Input}, for the fields that hold prose —
 * a backstory, a high concept, what an item does.
 *
 * It grows with its content instead of scrolling. A single-line box turns a
 * paragraph into a slot you read through a keyhole, which on a phone means
 * scrolling sideways word by word to check what you already wrote. Growing
 * keeps the whole text on screen, which is the only reason this component
 * exists separately from a bare `<textarea>`.
 *
 * It never scrolls the page itself. An earlier version moved the focused field up under
 * the header on touch devices, which fought the browser's own scrolling, the keyboard's
 * animation and anyone who had already scrolled where they wanted to be. Where the page
 * goes on focus is the browser's decision; this component only sizes itself, and the CSS
 * caps its height at the room left above the keyboard so it cannot grow down into it.
 */
export function Textarea({
  label,
  hint,
  invalid = false,
  className,
  id,
  value,
  ...rest
}: TextareaProps) {
  const generatedId = useId()
  const textareaId = id ?? generatedId
  const hintId = `${textareaId}-hint`
  const ref = useRef<HTMLTextAreaElement>(null)

  // `field-sizing: content` does this natively where it is supported. This is the
  // fallback for everyone else: measure the content and match the box to it.
  // Setting height to 'auto' first is what lets the box shrink again on delete.
  const fit = useCallback(() => {
    const element = ref.current
    if (element === null) return
    if (CSS.supports('field-sizing', 'content')) return

    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
  }, [])

  // Re-fit on every value change, not just on typing: text arriving from outside
  // (opening a form, an AI-generated draft) has to size the box too.
  useEffect(fit, [fit, value])

  return (
    <div className={cx(styles.field, className)}>
      <label htmlFor={textareaId}>
        <SectionLabel tone="muted" micro>
          {label}
        </SectionLabel>
      </label>
      <textarea
        ref={ref}
        id={textareaId}
        className={cx(styles.textarea, invalid && styles.invalid)}
        aria-invalid={invalid || undefined}
        aria-describedby={hint !== undefined ? hintId : undefined}
        value={value}
        {...rest}
      />
      {hint !== undefined && (
        <p id={hintId} className={cx(styles.hint, invalid && styles.hintInvalid)}>
          {hint}
        </p>
      )}
    </div>
  )
}
